/**
 * Interleaved RLE decompression (MS-RDPBCGR 3.1.9), 16bpp. A direct port of the
 * gateway's Go decoder. "Interleaved" because most orders encode pixels as a
 * delta against the pixel directly above, so unchanged screen costs almost
 * nothing.
 */

// Regular orders carry a 3-bit code, lite orders a 4-bit code, and the
// MEGA_MEGA and special orders use the whole byte.
const CODE_REGULAR_BG_RUN = 0x00;
const CODE_REGULAR_FG_RUN = 0x01;
const CODE_REGULAR_FGBG_IMAGE = 0x02;
const CODE_REGULAR_COLOR_RUN = 0x03;
const CODE_REGULAR_COLOR_IMAGE = 0x04;
const CODE_LITE_SET_FG_FG_RUN = 0x0c;
const CODE_LITE_SET_FG_FGBG_IMAGE = 0x0d;
const CODE_LITE_DITHERED_RUN = 0x0e;

const CODE_MEGA_BG_RUN = 0xf0;
const CODE_MEGA_FG_RUN = 0xf1;
const CODE_MEGA_FGBG_IMAGE = 0xf2;
const CODE_MEGA_COLOR_RUN = 0xf3;
const CODE_MEGA_COLOR_IMAGE = 0xf4;
const CODE_MEGA_SET_FG_RUN = 0xf6;
const CODE_MEGA_SET_FGBG_IMAGE = 0xf7;
const CODE_MEGA_DITHERED_RUN = 0xf8;
const CODE_SPECIAL_FGBG_1 = 0xf9;
const CODE_SPECIAL_FGBG_2 = 0xfa;
const CODE_WHITE = 0xfd;
const CODE_BLACK = 0xfe;

const PIXEL_WHITE = 0xffff;
const PIXEL_BLACK = 0x0000;

/**
 * Reads one byte, refusing to run off the end of the stream. Treating a missing
 * byte as zero would decode into plausible-looking pixels and hide the
 * corruption.
 */
function byteAt(src: Uint8Array, index: number): number {
  const value = src[index];

  if (value === undefined) {
    throw new Error(`rle: truncated stream at byte ${index}`);
  }

  return value;
}

/** Recovers the order code from a header byte. */
export function extractCodeId(header: number): number {
  if ((header & 0xc0) !== 0xc0) {
    return header >> 5; // regular: 3-bit code
  }

  if ((header & 0xf0) === 0xf0) {
    return header; // MEGA_MEGA and special orders
  }

  return header >> 4; // lite: 4-bit code
}

export interface RunLength {
  length: number;
  advance: number;
}

/**
 * Returns a run's length and how many bytes its header occupied.
 *
 * A zero length field does not mean "zero": it means the extended form, where
 * the length is the next byte plus a bias. Reading it as zero silently corrupts
 * every order that follows.
 */
export function extractRunLength(code: number, b: Uint8Array): RunLength {
  switch (code) {
    case CODE_REGULAR_FGBG_IMAGE: {
      const length = byteAt(b, 0) & 0x1f;
      if (length === 0) return { length: byteAt(b, 1) + 1, advance: 2 };

      return { length: length * 8, advance: 1 };
    }

    case CODE_LITE_SET_FG_FGBG_IMAGE: {
      const length = byteAt(b, 0) & 0x0f;
      if (length === 0) return { length: byteAt(b, 1) + 1, advance: 2 };

      return { length: length * 8, advance: 1 };
    }

    case CODE_MEGA_BG_RUN:
    case CODE_MEGA_FG_RUN:
    case CODE_MEGA_FGBG_IMAGE:
    case CODE_MEGA_COLOR_RUN:
    case CODE_MEGA_COLOR_IMAGE:
    case CODE_MEGA_SET_FG_RUN:
    case CODE_MEGA_SET_FGBG_IMAGE:
    case CODE_MEGA_DITHERED_RUN:
      return { length: byteAt(b, 1) | (byteAt(b, 2) << 8), advance: 3 };

    case CODE_LITE_SET_FG_FG_RUN:
    case CODE_LITE_DITHERED_RUN: {
      const length = byteAt(b, 0) & 0x0f;
      if (length === 0) return { length: byteAt(b, 1) + 16, advance: 2 };

      return { length, advance: 1 };
    }

    default: {
      const length = byteAt(b, 0) & 0x1f;
      if (length === 0) return { length: byteAt(b, 1) + 32, advance: 2 };

      return { length, advance: 1 };
    }
  }
}

/** Accumulates decoded pixels and answers "what is directly above". */
class Canvas {
  readonly pixels: Uint16Array;
  private index = 0;

  constructor(
    private readonly width: number,
    height: number,
  ) {
    this.pixels = new Uint16Array(width * height);
  }

  get full(): boolean {
    return this.index >= this.pixels.length;
  }

  get onFirstLine(): boolean {
    return this.index < this.width;
  }

  above(): number {
    return this.index < this.width
      ? 0
      : (this.pixels[this.index - this.width] ?? 0);
  }

  write(pixel: number): void {
    if (this.index >= this.pixels.length) {
      throw new Error(`rle: output overrun at pixel ${this.index}`);
    }

    this.pixels[this.index] = pixel;
    this.index += 1;
  }
}

/** Decompresses one 16bpp bitmap rectangle into RGB565 pixels. */
export function decodeInterleavedRle(
  src: Uint8Array,
  width: number,
  height: number,
): Uint16Array {
  const canvas = new Canvas(width, height);
  let fgPel = PIXEL_WHITE;
  let insertFgPel = false;
  let at = 0;

  const readPixel = (offset: number): number =>
    byteAt(src, offset) | (byteAt(src, offset + 1) << 8);

  /** Writes `count` pixels from a one-byte foreground/background mask. */
  const writeFgBgRun = (mask: number, count: number): void => {
    for (let i = 0; i < count; i += 1) {
      const set = (mask & (1 << (i % 8))) !== 0;

      if (canvas.onFirstLine) {
        canvas.write(set ? fgPel : 0);
      } else {
        const above = canvas.above();
        canvas.write(set ? above ^ fgPel : above);
      }
    }
  };

  while (at < src.length && !canvas.full) {
    const header = byteAt(src, at);
    const code = extractCodeId(header);
    const rest = src.subarray(at);

    // Background runs first.
    // They are the only order that can carry an implied foreground pixel from the run before it.
    if (code === CODE_REGULAR_BG_RUN || code === CODE_MEGA_BG_RUN) {
      const { length, advance } = extractRunLength(code, rest);
      at += advance;

      let remaining = length;

      if (insertFgPel) {
        canvas.write(canvas.onFirstLine ? fgPel : canvas.above() ^ fgPel);
        remaining -= 1;
      }

      for (let i = 0; i < remaining; i += 1) {
        canvas.write(canvas.onFirstLine ? 0 : canvas.above());
      }

      // Two background runs in a row imply a foreground pixel between them.
      insertFgPel = true;

      continue;
    }

    insertFgPel = false;

    switch (code) {
      case CODE_REGULAR_FG_RUN:
      case CODE_MEGA_FG_RUN:
      case CODE_LITE_SET_FG_FG_RUN:
      case CODE_MEGA_SET_FG_RUN: {
        const { length, advance } = extractRunLength(code, rest);
        at += advance;

        if (code === CODE_LITE_SET_FG_FG_RUN || code === CODE_MEGA_SET_FG_RUN) {
          fgPel = readPixel(at);
          at += 2;
        }

        for (let i = 0; i < length; i += 1) {
          canvas.write(canvas.onFirstLine ? fgPel : canvas.above() ^ fgPel);
        }

        break;
      }

      case CODE_LITE_DITHERED_RUN:
      case CODE_MEGA_DITHERED_RUN: {
        const { length, advance } = extractRunLength(code, rest);
        at += advance;

        const pixelA = readPixel(at);
        const pixelB = readPixel(at + 2);
        at += 4;

        for (let i = 0; i < length; i += 1) {
          canvas.write(pixelA);
          canvas.write(pixelB);
        }

        break;
      }

      case CODE_REGULAR_COLOR_RUN:
      case CODE_MEGA_COLOR_RUN: {
        const { length, advance } = extractRunLength(code, rest);
        at += advance;

        const pixel = readPixel(at);
        at += 2;

        for (let i = 0; i < length; i += 1) {
          canvas.write(pixel);
        }

        break;
      }

      case CODE_REGULAR_FGBG_IMAGE:
      case CODE_MEGA_FGBG_IMAGE:
      case CODE_LITE_SET_FG_FGBG_IMAGE:
      case CODE_MEGA_SET_FGBG_IMAGE: {
        const { length, advance } = extractRunLength(code, rest);
        at += advance;

        if (
          code === CODE_LITE_SET_FG_FGBG_IMAGE ||
          code === CODE_MEGA_SET_FGBG_IMAGE
        ) {
          fgPel = readPixel(at);
          at += 2;
        }

        let remaining = length;

        while (remaining > 0) {
          const run = Math.min(remaining, 8);
          writeFgBgRun(byteAt(src, at), run);
          at += 1;
          remaining -= run;
        }

        break;
      }

      case CODE_REGULAR_COLOR_IMAGE:
      case CODE_MEGA_COLOR_IMAGE: {
        const { length, advance } = extractRunLength(code, rest);
        at += advance;

        for (let i = 0; i < length; i += 1) {
          canvas.write(readPixel(at + i * 2));
        }

        at += length * 2;

        break;
      }

      case CODE_SPECIAL_FGBG_1:
        at += 1;
        writeFgBgRun(0x03, 8);
        break;

      case CODE_SPECIAL_FGBG_2:
        at += 1;
        writeFgBgRun(0x05, 8);
        break;

      case CODE_WHITE:
        at += 1;
        canvas.write(PIXEL_WHITE);
        break;

      case CODE_BLACK:
        at += 1;
        canvas.write(PIXEL_BLACK);
        break;

      default:
        throw new Error(
          `rle: unknown order code 0x${code.toString(16)} (header 0x${header.toString(16)})`,
        );
    }
  }

  return canvas.pixels;
}
